import React, { useState, useEffect, useMemo } from 'react';
import { getContracts } from '../../services/api';

function formatCurrency(value) {
  if (value === null || value === undefined || value === '') return '--';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);
}

function Companies({ onNavigate }) {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    async function load() {
      try {
        const data = await getContracts();
        setContracts(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const customers = useMemo(() => {
    const map = {};

    contracts.forEach((c) => {
      const key = c.endCustomer || c.customer;
      if (!key) return;

      if (!map[key]) {
        map[key] = {
          name: key,
          contractCount: 0,
          totalValue: 0,
          contractTypes: new Set(),
          divisions: new Set(),
          primes: new Set(),
        };
      }

      const entry = map[key];
      entry.contractCount += 1;
      entry.totalValue += Number(c.contractValue) || 0;
      if (c.contractType) entry.contractTypes.add(c.contractType);
      if (c.division) entry.divisions.add(c.division);
      if (c.customer) entry.primes.add(c.customer);
    });

    return Object.values(map)
      .map((entry) => ({
        ...entry,
        contractTypes: Array.from(entry.contractTypes).sort(),
        divisions: Array.from(entry.divisions).sort(),
        primes: Array.from(entry.primes).sort(),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [contracts]);

  const filtered = useMemo(() => {
    if (!search.trim()) return customers;
    const q = search.toLowerCase();
    return customers.filter((c) =>
      c.name.toLowerCase().includes(q)
      || c.primes.some((p) => p.toLowerCase().includes(q))
    );
  }, [customers, search]);

  return (
    <div>
      {/* Toolbar */}
      <div className="page-toolbar">
        <div className="page-toolbar-left">
          <div className="search-wrapper">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              className="search-input"
              placeholder="Search customers..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div style={{
          padding: '40px',
          textAlign: 'center',
          color: 'var(--bcore-text-dim)',
          textTransform: 'uppercase',
          letterSpacing: '1px',
          fontSize: '13px',
        }}>
          Loading customers...
        </div>
      )}

      {/* Error State */}
      {error && (
        <div style={{
          padding: '40px',
          textAlign: 'center',
          color: 'var(--status-critical)',
          fontSize: '13px',
        }}>
          {error}
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && filtered.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          color: 'var(--bcore-text-dim)',
          fontSize: '13px',
        }}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ marginBottom: '16px', opacity: 0.4 }}>
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
            <polyline points="9 22 9 12 15 12 15 22" />
          </svg>
          <div style={{ textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>
            {customers.length === 0 ? 'No customer data' : 'No matching customers'}
          </div>
          <div style={{ fontSize: '12px' }}>
            {customers.length === 0
              ? 'Customer data is derived from contracts'
              : 'Try adjusting your search'}
          </div>
        </div>
      )}

      {/* Customer Cards Grid */}
      {!loading && !error && filtered.length > 0 && (
        <div className="customer-cards">
          {filtered.map((customer) => (
            <div
              className="customer-card"
              key={customer.name}
              onClick={() => onNavigate('customer-detail', { customerName: customer.name })}
              style={{ cursor: 'pointer' }}
            >
              <div className="customer-card-header">
                <div>
                  <div className="customer-card-name">{customer.name}</div>
                </div>
              </div>
              <div className="customer-card-body">
                <div className="customer-card-tags">
                  {customer.contractTypes.map((type) => (
                    <span className="tag" key={type}>{type}</span>
                  ))}
                </div>
                {customer.primes.length > 0 && (
                  <div className="customer-card-primes">
                    {customer.primes.map((prime) => (
                      <span className="customer-prime-tag" key={prime}>{prime}</span>
                    ))}
                  </div>
                )}
                <div className="customer-card-stats">
                  <div className="customer-stat">
                    <div className="customer-stat-value">{customer.contractCount}</div>
                    <div className="customer-stat-label">Contracts</div>
                  </div>
                  <div className="customer-stat">
                    <div className="customer-stat-value">{formatCurrency(customer.totalValue)}</div>
                    <div className="customer-stat-label">Portfolio Value</div>
                  </div>
                  <div className="customer-stat">
                    <div className="customer-stat-value">{customer.divisions.length}</div>
                    <div className="customer-stat-label">Divisions</div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default Companies;
